import { usePage } from "@inertiajs/react";
import IdvVerificationAlert from "@/components/IdvVerificationAlert";
import Layout from "@/layouts/layout";
import LoggedHours from "@/components/home/LoggedHours";
import Announcements from "@/components/home/Announcements";
import { Announcement } from "@/interfaces/announcement";
import { Goal } from "@/interfaces/goal";
import MissingAccountFields from "@/components/settings/MissingAccountFields";
import OnboardingForced from "@/pages/onboarding-forced";
import WelcomeTour from "@/components/onboarding/WelcomeTour";
import SpeechHeading from "@/components/ui/SpeechHeading";
import NextStep from "@/components/home/NextStep";
import ProgramCountdown from "@/components/home/ProgramCountdown";
import Panel from "@/components/ui/Panel";
import Card from "@/components/ui/Card";
import Badge from "@/components/ui/Badge";
import { Link } from "@inertiajs/react";
import type { SatelliteEvent } from "@/interfaces/satellite_event";
import type { SharedProps } from "@/types";

export default function Home() {
  const { props } = usePage<
    SharedProps & {
      totalProjectTime: number;
      inProgressTime: number;
      reviewTime: number;
      projectCount: number;
      announcements: Announcement[];
      goals: Goal[];
      boughtInvite: boolean;
      my_events: SatelliteEvent[];
      my_rsvps: SatelliteEvent[];
      upcoming: SatelliteEvent[];
    }
  >();

  // Hackatime onboarding only matters when the ship-hours program is on.
  if (
    props.program.projects_enabled &&
    !props.user?.onboarding_completed &&
    !props.user?.hackatime_id
  ) {
    return <OnboardingForced />;
  }

  const program = props.program;
  const myEvents = props.my_events ?? [];
  const myRsvps = props.my_rsvps ?? [];
  const upcoming = props.upcoming ?? [];

  return (
    <Layout>
      {/* First sign-in only. The dashboard is mostly empty until someone RSVPs
          or applies, so explain the program before showing it to them. */}
      {program.events_enabled && !props.user?.onboarding_completed && (
        <WelcomeTour program={program} name={props.user.display_name} />
      )}

      <div className="flex flex-col gap-8">
        <SpeechHeading
          title={`Welcome, ${props.user.display_name}!`}
          subtitle={program.tagline}
        />

        <MissingAccountFields />
        <IdvVerificationAlert />

        {program.events_enabled && (
          <>
            <ProgramCountdown program={program} />

            <NextStep events={myEvents} rsvps={myRsvps} program={program} />
            {myEvents.length > 0 && (
              <Panel title={`your-events.csv - ${myEvents.length}`}>
                <div className="flex flex-col">
                  {myEvents.map((e) => {
                    const p = e.checklist_progress;
                    const pct =
                      p && p.total > 0
                        ? Math.round((p.done / p.total) * 100)
                        : null;

                    return (
                      <Link
                        key={e.id}
                        href={`/organize/${e.id}`}
                        className="border-line/20 hover:bg-goldenrod/20 duration-fast ease-standard -mx-2 flex flex-col gap-2 border-b px-2 py-3 transition-colors last:border-b-0"
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-body text-ink min-w-0 flex-1 truncate font-bold">
                            {e.name}
                          </span>

                          {(e.unread_for_organizer ?? 0) > 0 && (
                            <Badge tone="warning">
                              {e.unread_for_organizer} from HQ
                            </Badge>
                          )}

                          {e.aasm_state === "approved" ? (
                            <Badge tone="success">
                              {e.rsvp_count ?? 0} going
                            </Badge>
                          ) : (
                            <Badge tone="info">
                              {e.aasm_state === "submitted"
                                ? "Under review"
                                : e.aasm_state === "rejected"
                                  ? "Needs changes"
                                  : "Draft"}
                            </Badge>
                          )}
                        </div>

                        <p className="text-caption text-ink-muted">
                          {e.location_label}
                          {" · "}
                          {e.starts_at_short_label ?? "Date TBD"}
                        </p>

                        {/* Progress only means something once they're approved
                            and actually have a checklist. */}
                        {e.aasm_state === "approved" && pct !== null && (
                          <div className="flex items-center gap-2">
                            <div className="border-line/40 bg-surface-plain h-2 flex-1 overflow-hidden border">
                              <div
                                className="bg-leaf h-full"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className="text-caption text-ink-soft shrink-0">
                              {p!.done}/{p!.total}
                            </span>
                          </div>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </Panel>
            )}

            {myRsvps.length > 0 && (
              <Panel title="you-re-going.csv">
                <div className="flex flex-col gap-3">
                  {myRsvps.map((e) => (
                    <Link
                      key={e.id}
                      href={`/events/${e.id}`}
                      className="border-line/20 flex flex-wrap items-center gap-3 border-b pb-3 last:border-b-0 last:pb-0"
                    >
                      <span className="text-body text-ink min-w-0 flex-1 truncate font-bold">
                        {e.name}
                      </span>
                      <span className="text-caption text-ink-muted">
                        {e.location_label} ·{" "}
                        {e.starts_at_short_label ?? "Date TBD"}
                      </span>
                    </Link>
                  ))}
                </div>
              </Panel>
            )}

            {upcoming.length > 0 && (
              <section className="flex flex-col gap-4">
                <h2 className="font-display text-heading text-ink font-bold">
                  Coming up
                </h2>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {upcoming.map((e) => (
                    <Link key={e.id} href={`/events/${e.id}`}>
                      <Card interactive>
                        <h3 className="font-display text-subheading text-ink font-bold">
                          {e.name}
                        </h3>
                        <p className="text-caption text-ink-muted mt-1">
                          {e.location_label} ·{" "}
                          {e.starts_at_short_label ?? "Date TBD"}
                        </p>
                        <div className="mt-auto pt-3">
                          <Badge tone="neutral">
                            {e.rsvp_count ?? 0} going
                          </Badge>
                        </div>
                      </Card>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        {program.shop_enabled && props.goals?.length > 0 && (
          <Panel title="your-tickets.csv">
            <LoggedHours tickets={props.user.balance} goals={props.goals} />
          </Panel>
        )}

        <Announcements announcements={props.announcements} />
      </div>
    </Layout>
  );
}
